import React, { useState } from 'react';
import { Link } from 'gatsby';
import TabbedLogoBar from 'components/frame/tabbed-logo-bar';
import { ReactComponent as ImgWSFECLogo } from 'img/logos/full-logo-wsfec.svg';
import { ReactComponent as ImgStateFairLogo } from 'img/logos/full-logo-wsf.svg';
import { ReactComponent as ImgSpringFairLogo } from 'img/logos/full-logo-wsf-spring.svg';
import { ReactComponent as ImgOktoberfestLogo } from 'img/logos/full-logo-oktoberfest.svg';
import { ReactComponent as ImgHolidayMagicLogo } from 'img/logos/full-logo-holiday-magic.svg';
import { ReactComponent as ImgTasteNWLogo } from 'img/logos/full-logo-taste.svg';
import { ReactComponent as ImgFoundationLogo } from 'img/logos/full-logo-foundation.svg';
import { ReactComponent as ImgNavExpander } from 'img/arrows/caret-down-white.svg';
import { ReactComponent as ImgNavCalendar } from 'img/icons/icon-nav-calendar.svg';
import { ReactComponent as ImgSearchIcon } from 'img/icons/icon-nav-search.svg';
import type { MainNavData, NavTreeFields } from "hooks/use-site-section-navigation-data"
import { useGenerateSiteLink } from 'hooks/use-generate-site-link';
import SearchModalBuilder from 'helpers/builders/search-modal-builder';
import { navigate } from 'gatsby';
import { getLinkForGlobalEventsCalendar } from 'hooks/use-generate-site-link';

interface HeaderProps extends React.PropsWithChildren {
    mainNavData?: MainNavData;
    currentPageId?: string;
}

const renderNavLogo = (logoName: string) => {
    let logo = <ImgWSFECLogo />;
    switch (logoName) {
        case 'full-logo-wsf.svg':
            logo = <ImgStateFairLogo />;
            break;
        case 'full-logo-wsf-spring.svg':
            logo = <ImgSpringFairLogo />;
            break;
        case 'full-logo-oktoberfest.svg':
            logo = <ImgOktoberfestLogo />;
            break;
        case 'full-logo-holiday-magic.svg':
            logo = <ImgHolidayMagicLogo />;
            break;
        case 'full-logo-taste.svg':
            logo = <ImgTasteNWLogo />;
            break;
        case 'full-logo-foundation.svg':
            logo = <ImgFoundationLogo />;
            break;
    }

    return logo;
};

const sortNavItems = (a: NavTreeFields, b: NavTreeFields) => {
    return (a.position ?? 0) - (b.position ?? 0);
};

const isNavItemActive = (currentPageId: string, navItem: NavTreeFields) => {
    // If this isn't an external link, check the current page against the linked model id 
    if (navItem.link?.__typename !== 'DatoCmsExternalLink' && navItem.link?.link?.id === currentPageId) {
        return true;
    }

    // Check children for an item with a matching linked model id
    if (!!navItem.treeChildren && navItem.treeChildren.length > 0) {
        return navItem.treeChildren.some(childNavItem => childNavItem.link?.__typename !== 'DatoCmsExternalLink' && childNavItem.link?.link?.id === currentPageId);
    }

    return false;
};

// TODO: This can be done in a more React-y way, but for now we're sticking what was in the static FE templates
const toggleMobileMenuDrawer = (event: React.MouseEvent | React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const element = event.target as HTMLButtonElement;
    element.classList.toggle('menu-btn--active');
    document.querySelector('body')?.classList.toggle('menu-open');
};

// TODO: This can be done in a more React-y way, but for now we're sticking what was in the static FE templates
const toggleMobileSubnavExpander = (event: React.MouseEvent) => {
    event.preventDefault();

    const element = event.target as HTMLElement;
    const currentLi = element.parentNode as HTMLElement;
    const subnavElement  = currentLi.querySelector(':scope > .main-nav__second');

    if( currentLi.classList.contains('toggle') ){
        
        currentLi.classList.remove('toggle');
        if (subnavElement) {
            slideClose(subnavElement as HTMLElement);
        }

    } else {
        currentLi.classList.add('toggle');
        if (subnavElement) {
            slideOpen(subnavElement as HTMLElement);
        }
    }
};

// TODO: This can be done in a more React-y way, but for now we're sticking what was in the static FE templates
const slideOpen = (elem: HTMLElement) => {
	// Get the natural height of the element
    // Even though we’re showing and then re-hiding our content, it never shows visibly for users because 
    // the function runs so quickly (just a few milliseconds) and thus never actually renders visually in the DOM.
	const getHeight = function () {
		elem.style.display = 'block'; // Make it visible
		const height = elem.scrollHeight + 'px'; // Get it's height
		elem.style.display = ''; //  Hide it again
		return height;
	};

	const height = getHeight(); // Get the natural height
	elem.classList.add('is-visible'); // Make the element visible
	elem.style.height = height; // Update the max-height
    elem.style.overflow = 'hidden';

    // Once the transition is complete, remove the inline max-height so the content can scale responsively
	window.setTimeout(function () {
		elem.style.height = '';
        elem.style.overflow = '';
	}, 300);

};

// TODO: This can be done in a more React-y way, but for now we're sticking what was in the static FE templates
//slide out to hide
const slideClose = (elem: HTMLElement) => {
	// Give the element a height to change from
	elem.style.height = elem.scrollHeight + 'px';
    elem.style.overflow = 'hidden';

	// Set the height back to 0
	window.setTimeout(function () {
		elem.style.height = '0';
	}, 1);

	// When the transition is complete, hide it
	window.setTimeout(function () {
		elem.classList.remove('is-visible');
        elem.style.height = '';
        elem.style.overflow = '';
	}, 300);

};

const Header: React.FC<HeaderProps> = ({mainNavData, currentPageId}) => {

    const openModal = (e: React.SyntheticEvent) => {
        e.preventDefault();
        setIsModalOpen(true);
    };

    const calendarPath = getLinkForGlobalEventsCalendar() ?? '/calendar';

    const [isModalOpen, setIsModalOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(event.target.value);
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {

        event.preventDefault();

        toggleMobileMenuDrawer(event);

        // gatsby navigate to search page with `q=keyword`
        navigate(`/search?q=${searchTerm}`);

    }

    return (
        <header className="site-header" data-datocms-noindex>

            <TabbedLogoBar tab={mainNavData?.tabName ?? 'wsfec'} />
            {/* add main-header-bar.tsx */}
            <div className="main-header-bar">
                <div className="container">
                    <Link className="logo" to={mainNavData?.navLogo.path ?? '/'} title={mainNavData?.navLogo.title ?? 'Washington State Fair Events Center'}>
                        {renderNavLogo(mainNavData?.navLogo.logo ?? '')}
                    </Link>

                    <button className="menu-btn" onClick={toggleMobileMenuDrawer}>
                        <div className="toggle">
                            <span></span><span></span><span></span><span></span>
                        </div>
                        <span>Menu</span>
                    </button>

                    <nav className="main-nav" aria-label="Site Main Navigation">
                        <ul className="main-nav__menu" role="list">
                            {mainNavData?.navTree && mainNavData.navTree.length > 0 && mainNavData.navTree.toSorted(sortNavItems).map(navItem => {
                                const hasChildren = !!navItem.treeChildren && navItem.treeChildren.length > 0;
                                const isActive = currentPageId && isNavItemActive(currentPageId, navItem);
                                const link = useGenerateSiteLink(navItem.link);
                                return (
                                    <li key={navItem.id} className={`menu-item ${hasChildren ? 'menu-item--has-children' : ''} ${isActive ? 'current-menu-item' : ''}`}>
                                        {hasChildren &&
                                            <button className="nav-expander" onClick={toggleMobileSubnavExpander}>
                                                <span className="sr-only">Expand Subnav</span>
                                                <ImgNavExpander />
                                            </button>
                                        }
                                        
                                        <a href={link?.url ?? '#'}>{link?.label ?? navItem.label}</a>

                                        {hasChildren && 
                                            <ul className="main-nav__second" role="list">
                                                {navItem.treeChildren?.toSorted(sortNavItems).map(child => {
                                                    const childLink = useGenerateSiteLink(child.link);
                                                    return (
                                                        <li key={child.id}>
                                                            <a href={childLink?.url ?? '#'}>{childLink?.label ?? child.label}</a>
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        }
                                    </li>
                                );
                            })}

                            <li className="nav-search">
                                <button className="js-open-search" onClick={openModal}>
                                    <ImgSearchIcon />
                                    <span>Search</span>
                                </button>
                                <SearchModalBuilder 
                                    headline='Search' 
                                    isOpen={isModalOpen} 
                                    closeModal={() => {setIsModalOpen(false)}} 
                                />
                                <form onSubmit={handleFormSubmit}>
                                    <label htmlFor="menu-search">Search</label>
                                    <div className="nav-search-fields">
                                        <input type="text" name="search" id="menu-search" value={searchTerm} onChange={handleInputChange} />
                                        <button type="submit">
                                            <ImgSearchIcon />
                                            <span className="sr-only">Search</span>
                                        </button>
                                    </div>
                                </form>
                            </li>
                        </ul>

                        <Link className="calendar" to={calendarPath}>
                            <ImgNavCalendar />
                        </Link>
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default Header;